import { endOfDay, startOfDay, subDays } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetMaintenanceAnalytics } from '@/api/maintenance/get-maintenance-analytics'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Typography } from '@/components/ui/typography'
import { DateRange } from '@/models/helpers'

import { getMaintenanceDataReportCards } from './maintenance-data-reports.constants'
import * as S from './maintenance-data-reports.styled'

export interface MaintenanceDataReportsProps {
  buildingId?: number
}

const getDefaultRange = (): DateRange => [
  subDays(startOfDay(new Date()), 30),
  startOfDay(new Date()),
]

export const MaintenanceDataReports = ({ buildingId }: MaintenanceDataReportsProps) => {
  const { t } = useTranslation(['maintenance'])
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultRange)
  const [fromDate, toDate] = dateRange

  const { data } = useGetMaintenanceAnalytics({
    buildingId,
    endDate: fromZonedTime(toDate ? endOfDay(toDate) : new Date(), 'UTC').getTime(),
    startDate: fromZonedTime(fromDate ?? subDays(new Date(), 30), 'UTC').getTime(),
  })

  const cards = getMaintenanceDataReportCards(t)

  return (
    <S.Container data-component-id="MaintenanceDataReports">
      <S.Header>
        <Typography as="span" size="text-medium" weight="semibold" color="black800">
          {t('maintenance:requests.dataReports.title')}
        </Typography>
        <S.DateRangeWrapper>
          <DatePickerInput
            mode="range"
            placeholder={t('maintenance:requests.dataReports.selectTimeRange')}
            size="small"
            value={dateRange}
            maxDate={new Date()}
            onRangeChange={setDateRange}
          />
        </S.DateRangeWrapper>
      </S.Header>
      <S.CardsGrid>
        {cards.map((card) => (
          <S.ReportCard key={card.title}>
            <Typography as="span" size="text-small" color="black600">
              {card.title}
            </Typography>
            <Typography as="span" size="text-large" weight="semibold" color="black800">
              {data ? card.formatValue(data) : t('maintenance:requests.dataReports.notAvailable')}
            </Typography>
            <Typography as="span" size="text-small" color="black400">
              {card.description}
            </Typography>
          </S.ReportCard>
        ))}
      </S.CardsGrid>
    </S.Container>
  )
}
